import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { GetAccountQuery } from "types/graphql-schema";

import sendScheduledAiTestMutation from "@apps/account/graphql/send-scheduled-ai-test-mutation";
import updateUserMutation from "@apps/account/graphql/update-user-mutation";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  errorNotificationVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Heading from "@components/heading/heading";
import Link from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Placeholder from "@components/placeholder/placeholder";
import SettingsLayout from "@components/settings-layout/settings-layout";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";

import ProfileResponsiveTabs from "../profile/profile-responsive-tabs";
import { RoutineKey, findRoutine, isRoutineKey } from "./routines";

const ArrowLeftIcon = getTopicflowIcon("arrowLeft");

const formatHour = (hour: number) => {
  const period = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:00 ${period}`;
};

const hourOptions: ComboboxGenericOption<number>[] = Array.from(
  { length: 24 },
  (_, hour) => ({ value: hour, label: formatHour(hour) }),
);

const leadTimeOptions: ComboboxGenericOption<number>[] = [5, 10, 15, 30].map(
  (minutes) => ({ value: minutes, label: `${minutes} minutes before` }),
);

type Me = NonNullable<GetAccountQuery["me"]>;

type ConfigProps = {
  me: Me;
  onSendTest: (kind: "daily_brief" | "meeting_prep") => Promise<void>;
  sendingKind: string | null;
  hasSlackOrTeams: boolean;
};

const DailyBriefConfig = ({
  me,
  onSendTest,
  sendingKind,
  hasSlackOrTeams,
}: ConfigProps) => {
  const currentUser = currentUserVar();
  const userId = currentUser.id;
  const [hour, setHour] = useState<number>(me.dailyBriefHour ?? 9);
  const [updateUser] = useMutation(updateUserMutation);

  useEffect(() => {
    setHour(me.dailyBriefHour ?? 9);
  }, [me.dailyBriefHour]);

  const handleChangeHour = async (option: ComboboxGenericOption<number>) => {
    if (!option) return;
    const previous = hour;
    setHour(option.value);
    try {
      await updateUser({
        variables: { userId, dailyBriefHour: option.value },
      });
    } catch (e) {
      setHour(previous);
      errorNotificationVar({
        title: "Could not save settings",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <SettingsLayout.Container>
      <SettingsLayout.Row>
        <SettingsLayout.Left label="Time of day to send your daily brief, in your timezone.">
          Send at
        </SettingsLayout.Left>
        <SettingsLayout.Right>
          <div className="lg:w-72">
            <ComboboxGeneric<number>
              value={
                hourOptions.find((o) => o.value === hour) ?? hourOptions[9]
              }
              options={hourOptions}
              onChangeValue={handleChangeHour}
              aria-label="Daily brief hour select"
            />
          </div>
        </SettingsLayout.Right>
      </SettingsLayout.Row>

      <SettingsLayout.Row>
        <SettingsLayout.Left label="The skill that runs when this routine fires.">
          Skill
        </SettingsLayout.Left>
        <SettingsLayout.Right>
          <div className="text-sm text-gray-600">
            Runs your{" "}
            <code className="bg-gray-100 px-1 rounded">/daily-brief</code> skill
            — edit that skill to customize what&apos;s included.
          </div>
        </SettingsLayout.Right>
      </SettingsLayout.Row>

      {hasSlackOrTeams && (
        <SettingsLayout.Row>
          <SettingsLayout.Left label="Send a test daily brief now to your Slack or Teams.">
            Test
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <Button
              theme={buttonTheme.lightGray}
              size={ButtonSize.medium}
              text="Send test"
              aria-label="Send test daily brief"
              disabled={sendingKind !== null}
              onClick={() => onSendTest("daily_brief")}
            />
          </SettingsLayout.Right>
        </SettingsLayout.Row>
      )}
    </SettingsLayout.Container>
  );
};

const MeetingPrepConfig = ({
  me,
  onSendTest,
  sendingKind,
  hasSlackOrTeams,
}: ConfigProps) => {
  const currentUser = currentUserVar();
  const userId = currentUser.id;
  const label = useLabel();
  const [leadTime, setLeadTime] = useState<number>(
    me.meetingPrepLeadTimeMinutes ?? 10,
  );
  const [oneononeOnly, setOneononeOnly] = useState<boolean>(
    me.meetingPrepOneononeOnly ?? false,
  );
  const [updateUser] = useMutation(updateUserMutation);

  useEffect(() => {
    setLeadTime(me.meetingPrepLeadTimeMinutes ?? 10);
    setOneononeOnly(me.meetingPrepOneononeOnly ?? false);
  }, [me.meetingPrepLeadTimeMinutes, me.meetingPrepOneononeOnly]);

  const save = async (
    variables: Record<string, unknown>,
    rollback: () => void,
  ) => {
    try {
      await updateUser({ variables: { userId, ...variables } });
    } catch (e) {
      rollback();
      errorNotificationVar({
        title: "Could not save settings",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleChangeLeadTime = (option: ComboboxGenericOption<number>) => {
    if (!option) return;
    const previous = leadTime;
    setLeadTime(option.value);
    save({ meetingPrepLeadTimeMinutes: option.value }, () =>
      setLeadTime(previous),
    );
  };

  const handleToggleOneononeOnly = (next: boolean) => {
    const previous = oneononeOnly;
    setOneononeOnly(next);
    save({ meetingPrepOneononeOnly: next }, () => setOneononeOnly(previous));
  };

  return (
    <SettingsLayout.Container>
      <SettingsLayout.Row>
        <SettingsLayout.Left label="How long before each meeting your prep is delivered.">
          Send before meeting
        </SettingsLayout.Left>
        <SettingsLayout.Right>
          <div className="lg:w-72">
            <ComboboxGeneric<number>
              value={
                leadTimeOptions.find((o) => o.value === leadTime) ??
                leadTimeOptions[1]
              }
              options={leadTimeOptions}
              onChangeValue={handleChangeLeadTime}
              aria-label="Meeting prep lead time select"
            />
          </div>
        </SettingsLayout.Right>
      </SettingsLayout.Row>

      <SettingsLayout.Row>
        <SettingsLayout.Left label="Only send meeting prep for 1-on-1s.">
          {`Only ${label("oneonone", { pluralize: true })}`}
        </SettingsLayout.Left>
        <SettingsLayout.Right>
          <ToggleSwitch
            checked={oneononeOnly}
            onChange={handleToggleOneononeOnly}
            aria-label="Only 1-on-1s meeting prep"
          />
        </SettingsLayout.Right>
      </SettingsLayout.Row>

      <SettingsLayout.Row>
        <SettingsLayout.Left label="The skill that runs when this routine fires.">
          Skill
        </SettingsLayout.Left>
        <SettingsLayout.Right>
          <div className="text-sm text-gray-600">
            Runs your{" "}
            <code className="bg-gray-100 px-1 rounded">/meeting-prep</code>{" "}
            skill — edit that skill to customize what&apos;s included.
          </div>
        </SettingsLayout.Right>
      </SettingsLayout.Row>

      {hasSlackOrTeams && (
        <SettingsLayout.Row>
          <SettingsLayout.Left label="Send a test meeting prep now to your Slack or Teams.">
            Test
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <Button
              theme={buttonTheme.lightGray}
              size={ButtonSize.medium}
              text="Send test"
              aria-label="Send test meeting prep"
              disabled={sendingKind !== null}
              onClick={() => onSendTest("meeting_prep")}
            />
          </SettingsLayout.Right>
        </SettingsLayout.Row>
      )}
    </SettingsLayout.Container>
  );
};

const RoutineSessions = () => <Placeholder>No sessions yet.</Placeholder>;

const RoutineDetailInner = ({
  routineKey,
  me,
}: {
  routineKey: RoutineKey;
  me: Me;
}) => {
  const currentUser = currentUserVar();
  const userId = currentUser.id;
  const location = useLocation();
  const link = useLink();
  const { path, url } = useRouteMatch();
  const routine = findRoutine(me, routineKey);
  const [enabled, setEnabled] = useState<boolean>(routine.enabled);
  const [sendingKind, setSendingKind] = useState<string | null>(null);

  const [updateUser] = useMutation(updateUserMutation);
  const [sendTest] = useMutation(sendScheduledAiTestMutation);

  useEffect(() => {
    setEnabled(routine.enabled);
  }, [routine.enabled]);

  const hasSlackOrTeams = (me.organizations?.edges ?? []).some(
    (edge) =>
      edge?.node?.hasSlackConnection || edge?.node?.hasTeamsInstallation,
  );

  const enableField =
    routineKey === "daily-brief" ? "dailyBriefEnabled" : "meetingPrepEnabled";

  const handleToggleEnabled = async (next: boolean) => {
    const previous = enabled;
    setEnabled(next);
    try {
      await updateUser({ variables: { userId, [enableField]: next } });
    } catch (e) {
      setEnabled(previous);
      errorNotificationVar({
        title: "Could not save settings",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleSendTest = async (kind: "daily_brief" | "meeting_prep") => {
    setSendingKind(kind);
    try {
      await sendTest({ variables: { kind } });
      successNotificationVar({
        title: "Test sent",
        description:
          "This can take 30 seconds to a minute. Check your Slack or Teams.",
      });
    } catch (e) {
      errorNotificationVar({
        title: "Could not send test",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSendingKind(null);
    }
  };

  const onConfig =
    location.pathname === url ||
    location.pathname.startsWith(`${url}/configuration`);
  const onSessions = location.pathname.startsWith(`${url}/sessions`);

  const tabButtons: ToggleButtonGroupType<string>[] = [
    {
      value: "configuration",
      title: "Configuration",
      active: onConfig,
    },
    {
      value: "sessions",
      title: "Sessions",
      active: onSessions,
    },
  ];

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    link.redirect(`${url}/${button.value}`);
  };

  const configProps: ConfigProps = {
    me,
    onSendTest: handleSendTest,
    sendingKind,
    hasSlackOrTeams,
  };

  return (
    <div aria-label={`Routine ${routine.name}`}>
      <Link
        to="/agent/routines"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
      >
        <ArrowLeftIcon className="h-3.5 w-3.5" />
        <span>Routines</span>
      </Link>
      <div className="flex items-center justify-between gap-4 mb-4">
        <Heading small title={routine.name} />
        <ToggleSwitch
          checked={enabled}
          onChange={handleToggleEnabled}
          aria-label="Routine enabled toggle"
        />
      </div>
      <div className="mb-6">
        <ProfileResponsiveTabs buttons={tabButtons} onClick={handleTabClick} />
      </div>
      <Switch>
        <Route exact path={path}>
          <Redirect to={`${url}/configuration`} />
        </Route>
        <Route exact path={`${path}/configuration`}>
          {routineKey === "daily-brief" ? (
            <DailyBriefConfig {...configProps} />
          ) : (
            <MeetingPrepConfig {...configProps} />
          )}
        </Route>
        <Route exact path={`${path}/sessions`}>
          <RoutineSessions />
        </Route>
        <Route>
          <Redirect to={`${url}/configuration`} />
        </Route>
      </Switch>
    </div>
  );
};

const RoutineDetail = ({ me }: { me: Me }) => {
  const { routineKey } = useParams<{ routineKey: string }>();
  if (!isRoutineKey(routineKey)) {
    return <Redirect to="/agent/routines" />;
  }
  return <RoutineDetailInner routineKey={routineKey} me={me} />;
};

export default RoutineDetail;
